import { Pagination } from '@/domain/models'
import { AccountDependentSearch } from '@/domain/usecases/account/company-manager/account-dependent-search'
import { queryClient } from '@/infra/http'
import { parseNumber } from '@/main/helpers/number-helper'
import { decodeImportImage } from '@/main/routes/helpers/loaders'
import { QueryClient } from '@tanstack/react-query'

export type CompanyManagerDependentSearchLoaderType = {
  data: AccountDependentSearch.Model
  searchCriteria: AccountDependentSearch.RequestModel
  remote: AccountDependentSearch
  notFoundImage: HTMLImageElement
}

/**
 * Loads dependent search results for company manager
 * Parses search criteria from URL params and returns paginated results
 */
export async function loadCompanyManagerDependentSearch(
  request: Request
): Promise<CompanyManagerDependentSearchLoaderType> {
  const url = new URL(request.url)
  const searchCriteria = createSearchCriteria(url.searchParams)

  const [{ data, remote }, notFoundImage] = await Promise.all([
    promiseDependentSearch(searchCriteria, queryClient),
    decodeImportImage(import('@images/dependent.svg'))
  ])

  return {
    data,
    searchCriteria,
    remote,
    notFoundImage
  }
}

async function promiseDependentSearch(
  searchCriteria: AccountDependentSearch.RequestModel,
  queryClient: QueryClient
) {
  const module = await import(
    '@remote/account/company-manager/remote-company-manager-account-dependent-search-factory'
  )
  const makeRemote = module.makeRemoteCompanyManagerAccountDependentSearch
  const remote = makeRemote(queryClient)
  const data = await remote.loader(searchCriteria)

  return { data, remote }
}

function createSearchCriteria(searchParams: URLSearchParams): AccountDependentSearch.RequestModel {
  const name = searchParams.get(AccountDependentSearch.NAME_PARAM_NAME)
  const email = searchParams.get(AccountDependentSearch.EMAIL_PARAM_NAME)
  const page = searchParams.get(Pagination.PAGE_PARAM_NAME)
  const pageSize = searchParams.get(Pagination.PAGE_SIZE_PARAM_NAME)

  return {
    name,
    email,
    page: parseNumber(page, Pagination.DEFAULT_PAGE)!,
    pageSize: parseNumber(pageSize, AccountDependentSearch.DEFAULT_PAGE_SIZE)
  }
}
